import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Select, Input, Button, Tag, Divider, Popconfirm, message, Empty, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getAgendaItemNotes, createAgendaItemNote, updateAgendaItemNote, deleteAgendaItemNote,
} from '../../api/agendaItems';
import { NOTE_COLOR_OPTIONS, noteColorHex } from '../../constants/agendaNoteColors';

const { TextArea } = Input;

const AgendaNotesModal = ({ item, open, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [newContent, setNewContent] = useState('');
  const [newColor, setNewColor] = useState('red');
  const [submitting, setSubmitting] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editColor, setEditColor] = useState('red');

  const fetchNotes = useCallback(async () => {
    if (!item) return;
    setLoading(true);
    try {
      const res = await getAgendaItemNotes(item.id);
      setNotes(res.data.data || []);
    } catch {
      message.error('Notlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [item]);

  useEffect(() => {
    if (!open || !item) return;
    setNewContent('');
    setEditingId(null);
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const handleSubmitNote = async () => {
    if (!newContent.trim()) {
      message.warning('Not boş olamaz.');
      return;
    }
    setSubmitting(true);
    try {
      await createAgendaItemNote(item.id, { content: newContent.trim(), color: newColor });
      setNewContent('');
      fetchNotes();
    } catch {
      message.error('Not eklenemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditContent(note.content);
    setEditColor(note.color);
  };

  const handleSaveEdit = async (noteId) => {
    try {
      await updateAgendaItemNote(item.id, noteId, { content: editContent.trim(), color: editColor });
      setEditingId(null);
      fetchNotes();
    } catch {
      message.error('Not güncellenemedi.');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteAgendaItemNote(item.id, noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch {
      message.error('Not silinemedi.');
    }
  };

  return (
    <Modal
      title="Görev Notları"
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      {item && (
        <div className="mb-3 p-2 rounded text-sm text-gray-500" style={{ background: '#fafafa', whiteSpace: 'pre-wrap', maxHeight: 100, overflowY: 'auto' }}>
          {item.content}
          {item.is_completed && <Tag color="green" style={{ marginLeft: 8 }}>Tamamlandı</Tag>}
        </div>
      )}

      <div style={{ maxHeight: 320, overflowY: 'auto' }} className="mb-3">
        {loading ? (
          <div className="flex justify-center py-6"><Spin /></div>
        ) : notes.length === 0 ? (
          <Empty description="Not yok." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          notes.map((note) => (
            <div key={note.id} className="mb-2 p-2 rounded border" style={{ borderLeft: `3px solid ${noteColorHex(note.color)}` }}>
              {editingId === note.id ? (
                <div className="flex flex-col gap-2">
                  <TextArea value={editContent} onChange={(e) => setEditContent(e.target.value)} autoSize={{ minRows: 2, maxRows: 6 }} maxLength={2000} />
                  <div className="flex gap-2 items-center flex-wrap">
                    <Select value={editColor} onChange={setEditColor} style={{ width: 130 }}>
                      {NOTE_COLOR_OPTIONS.map((c) => (
                        <Select.Option key={c.value} value={c.value}>
                          <span style={{ color: c.hex }}>●</span> {c.label}
                        </Select.Option>
                      ))}
                    </Select>
                    <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleSaveEdit(note.id)}>Kaydet</Button>
                    <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingId(null)}>Vazgeç</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-400 mb-1">{dayjs(note.created_at).format('DD.MM.YYYY HH:mm')}</div>
                  <div style={{ color: noteColorHex(note.color), whiteSpace: 'pre-wrap' }}>{note.content}</div>
                  <div className="flex gap-2 mt-1">
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => startEdit(note)}>Düzenle</Button>
                    <Popconfirm title="Bu notu silmek istediğinize emin misiniz?" onConfirm={() => handleDeleteNote(note.id)} okText="Evet" cancelText="Hayır">
                      <Button size="small" type="text" danger icon={<DeleteOutlined />}>Sil</Button>
                    </Popconfirm>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <Divider style={{ margin: '8px 0' }} />

      <div className="flex flex-col gap-2">
        <TextArea
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleSubmitNote(); }}
          placeholder="Gelişmeyi yazın..."
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={2000}
        />
        <span className="text-xs text-gray-400">Ctrl + Enter ile hızlıca ekle</span>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={newColor} onChange={setNewColor} style={{ width: 130 }}>
            {NOTE_COLOR_OPTIONS.map((c) => (
              <Select.Option key={c.value} value={c.value}>
                <span style={{ color: c.hex }}>●</span> {c.label}
              </Select.Option>
            ))}
          </Select>
          <Button type="primary" icon={<PlusOutlined />} loading={submitting} onClick={handleSubmitNote}>
            Not Ekle
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AgendaNotesModal;
